import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bucket, GetSalesInsightsAggregatedRequest, SalesInsightsAggregate, useSalesInsightsAggregated } from '../../../../api/indicators';
import { dateRange, formatDateParam } from '../../../../helpers/dateUtils';
import { DashboardFiltersContext } from '../filters/DashboardFiltersContext';
import LineBarChart, { LineBarChartDataItem } from '../charts/lineBarChart/LineBarChart';
import { DashboardContext } from '../../DashboardContext';
import { SettingsContext } from '../../../../context/SettingsContext';
import { getBucketFromRange } from '../../dashboardHelpers';

const SalesIncTaxNetSalesExcTax = () => {
    const { t } = useTranslation();
    const { calendarPreferences } = useContext(SettingsContext);
    const { filters, dateRangeConfig } = useContext(DashboardFiltersContext);
    const { companyConfig, refreshDate } = useContext(DashboardContext);
    const [lineBarChartData, setLineBarChartData] = useState<LineBarChartDataItem[]>([]);

    const initialStartDate = useMemo(() => filters.periodFrom
        ? filters.periodFrom
        : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate, [filters.periodFrom]);

    const initialEndDate = useMemo(() => filters.periodTo
        ? filters.periodTo
        : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate, [filters.periodTo]);


    const [params, setParams] = useState<GetSalesInsightsAggregatedRequest>({
        fields: 'transactionVolume,netSales,totalSales',
        startDate: formatDateParam(initialStartDate),
        endDate: formatDateParam(initialEndDate),
        locations: filters?.locations?.join(',') ? filters?.locations?.join(',') : companyConfig?.locations?.map(location => location.id).join(','),
        locationArea: filters?.locationArea?.id,
        businessStartHour: calendarPreferences.daysStart,
        bucket: getBucketFromRange(initialStartDate, initialEndDate)
    });

    const {
        data: salesInsightsAggregate,
        isLoading,
        isError,
        refetch: fetchSalesAndTransactions
    } = useSalesInsightsAggregated<SalesInsightsAggregate>(params, 'sales-insights-aggregate-netSales');

    useEffect(() => {
        if (salesInsightsAggregate) {
            setLineBarChartData(
                salesInsightsAggregate.salesInsightsAggregate?.map((salesInsightsAggregateItem) => ({
                    dateTime: new Date(salesInsightsAggregateItem.dateTime),
                    "Sales (Including Tax)": salesInsightsAggregateItem.salesInsights.totalSalesIncTax || 0,
                    "Net Sales (Excluding Tax)": salesInsightsAggregateItem.salesInsights.netSalesExcTax || 0,
                    barData: salesInsightsAggregateItem.salesInsights.transactionVolume || 0
                }))
            );
        }
    }, [salesInsightsAggregate]);

    useEffect(() => {

        const start = filters.periodFrom
            ? filters.periodFrom
            : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate;

        const end = filters.periodTo
            ? filters.periodTo
            : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate;

        setParams(prev => ({
            ...prev,
            startDate: formatDateParam(start),
            endDate: formatDateParam(end),
            locations: filters?.locations?.join(','),
            locationArea: filters?.locationArea?.id,
            bucket: getBucketFromRange(start, end)
        }));
    }, [filters, dateRangeConfig]);

    useEffect(() => { fetchSalesAndTransactions(); }, [params, fetchSalesAndTransactions]);

    useEffect(() => {
        if (refreshDate != null) {
            fetchSalesAndTransactions();
        }
    }, [refreshDate, fetchSalesAndTransactions]);

    return (
        <LineBarChart
            title={t('screens.dashboarding.charts.salesIncTaxNetSalesExcTax')}
            currency={salesInsightsAggregate?.currency}
            isError={isError}
            isLoading={isLoading}
            rightAxisLabel={t("screens.dashboarding.charts.volume")}
            leftAxisLabel={t("screens.dashboarding.charts.amount")}
            lineLabels={[
                t("screens.dashboarding.charts.salesIncludingTax"),
                t("screens.dashboarding.charts.netSalesExcludingTax")
            ]}
            barLabel={t("screens.dashboarding.charts.volumeOfTransactions")}
            data={lineBarChartData}
            bucket={params.bucket as Bucket}
            setBucket={
                (value: string) => setParams({
                    ...params,
                    bucket: value as Bucket
                })
            }
            height={460}
        />
    )
}

export default SalesIncTaxNetSalesExcTax;

